import React, { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import AiChatbot from './AiChatbot';
import SessionNotes from './SessionNotes';
import LeftPanel from './AstrologerPanelLeft';
import ServiceSelect from './ServiceSelect';
import { API_BASE } from './PanelIcons';
import PastPredictionsReport from './PastPredictionsReport';
import './AstrologerPanel.css';

// ── Service short names for tabs ──────────────────────────────────────────────
const SERVICE_SHORT_NAMES = {
  pastPrediction:     'Past',
  futureConsultation: 'Future',
  remedies:           'Remedy',
  palmistry:          'Palm',
  astroVastu:         'Vastu',
  numerology:         'Nums',
  tarot:              'Tarot',
  compatibility:      'Compat',
  horoscopePDF:       'PDF',
  tenYearPDF:         '10yr',
};

// ── Service Configurations ─────────────────────────────────────────────────────
// buttons: null = use AiChatbot's built-in past/future layout
// buttons: array = service-specific button grid (bypasses mode toggle)
// type: 'future' routes to /future-predict, default routes to /predict-section

const SERVICE_CONFIGS = {
  pastPrediction: {
    label: 'Past Prediction', icon: '📖', mode: 'past',
    buttons: [
      { id: 'panchanga',    label: 'Panchanga' },
      { id: 'personality',  label: 'Personality' },
      { id: 'moles',        label: 'Moles & Palm' },
      { id: 'childhood',    label: 'Childhood' },
      { id: 'teenage',      label: 'Teenage' },
      { id: 'youngAdult',   label: 'Young Adult' },
      { id: 'recentYears',  label: 'Recent Years' },
      { id: 'loveMarriage', label: 'Love & Marriage' },
      { id: 'career',       label: 'Career' },
      { id: 'health',       label: 'Health' },
      { id: 'overall',      label: 'Overall' },
    ],
  },
  futureConsultation: {
    label: 'Future Consultation', icon: '🔮', mode: 'future',
    buttons: [
      { id: 'currentPhase',    label: '⚡ Current Phase',    type: 'future' },
      { id: 'next1year',       label: '📅 Next 1 Year',      type: 'future' },
      { id: 'next3years',      label: '🗓 Next 3 Years',     type: 'future' },
      { id: 'careerFuture',    label: '💼 Career Future',    type: 'future' },
      { id: 'marriageFuture',  label: '💑 Marriage Future',  type: 'future' },
      { id: 'financeFuture',   label: '💰 Finance',          type: 'future' },
      { id: 'healthFuture',    label: '🏥 Health Future',    type: 'future' },
      { id: 'tenYearOverview', label: '🌟 10 Year Overview', type: 'future' },
      { id: 'remedies',        label: '💊 Remedies',         type: 'future' },
    ],
  },
  remedies: {
    label: 'Remedies', icon: '💊', mode: 'remedies',
    buttons: [
      { id: 'personalRemedies',      label: '🌿 Personal Remedies' },
      { id: 'professionalRemedies',  label: '💼 Professional Remedies' },
      { id: 'mantraRecommendations', label: '🕉 Mantra Recommendations' },
      { id: 'gemstoneAnalysis',      label: '💎 Gemstone Analysis' },
      { id: 'doshaRemedies',         label: '⚠ Dosha Remedies' },
      { id: 'vtdSpecialServices',    label: '✦ VTD Special Services' },
    ],
  },
  palmistry: {
    label: 'Palmistry Reading', icon: '✋', mode: 'palmistry',
    extraFields: ['palmPhotos'],
    buttons: [
      { id: 'rightHand',      label: '✋ Right Hand Analysis' },
      { id: 'leftHand',       label: '🤚 Left Hand Analysis' },
      { id: 'lifeLine',       label: 'Life Line' },
      { id: 'heartLine',      label: 'Heart Line' },
      { id: 'headLine',       label: 'Head Line' },
      { id: 'fateLine',       label: 'Fate Line' },
      { id: 'mountsAnalysis', label: 'Mounts Analysis' },
      { id: 'overallPalm',    label: '✦ Overall Palm Reading' },
    ],
  },
  astroVastu: {
    label: 'Astro Vastu', icon: '🏠', mode: 'vastu',
    extraFields: ['vastuControls', 'housePhoto', 'floorPlan'],
    buttons: [
      { id: 'overallVastu',  label: '🏠 Overall Vastu'   },
      { id: 'mainEntrance',  label: '🚪 Main Entrance'   },
      { id: 'livingRoom',    label: '🛋 Living Room'     },
      { id: 'masterBedroom', label: '🛏 Master Bedroom'  },
      { id: 'kitchen',       label: '🍳 Kitchen'         },
      { id: 'poojaRoom',     label: '🪔 Pooja Room'      },
      { id: 'studyOffice',   label: '📚 Study/Office'    },
      { id: 'bathroom',      label: '🚿 Bathroom'        },
      { id: 'northZone',     label: '⬆ North Zone'      },
      { id: 'southZone',     label: '⬇ South Zone'      },
      { id: 'eastZone',      label: '➡ East Zone'       },
      { id: 'westZone',      label: '⬅ West Zone'       },
      { id: 'vastuRemedies', label: '💊 Vastu Remedies'  },
    ],
  },
  numerology: {
    label: 'Numerology', icon: '🔢', mode: 'numerology',
    extraFields: ['fullName', 'numerologyControls'],
    buttons: [
      { id: 'lifePathNumber',    label: '① Life Path Number'   },
      { id: 'destinyNumber',     label: '② Destiny Number'     },
      { id: 'soulNumber',        label: '③ Soul Number'        },
      { id: 'personalityNumber', label: '④ Personality Number' },
      { id: 'luckyNumbers',      label: '🍀 Lucky Numbers'     },
      { id: 'nameAnalysis',      label: '✍ Name Analysis'     },
      { id: 'currentYearNumber', label: '📅 Current Year'      },
      { id: 'bestDates',         label: '⭐ Best Dates'        },
    ],
  },
  tarot: {
    label: 'Tarot Reading', icon: '🃏', mode: 'tarot',
    extraFields: ['tarotControls', 'clientQuestion'],
    buttons: [
      { id: 'singleCard',        label: '🃏 Single Card'          },
      { id: 'threeCard',         label: '🃏 3 Card Spread'        },
      { id: 'pastPresentFuture', label: '⏳ Past-Present-Future'  },
      { id: 'loveReading',       label: '💖 Love Spread'          },
      { id: 'careerReading',     label: '💼 Career Spread'        },
      { id: 'yesNoQuestion',     label: '❓ Yes / No'             },
      { id: 'celticCross',       label: '✚ Celtic Cross'          },
      { id: 'monthlyGuidance',   label: '📅 Monthly Guidance'     },
    ],
  },
  compatibility: {
    label: 'Compatibility', icon: '💑', mode: 'compatibility',
    extraFields: ['compatPurpose', 'person2'],
    buttons: [
      { id: 'overallCompatibility',   label: '💑 Overall Match'        },
      { id: 'gunaMilan',              label: '⭐ Guna Milan Score'      },
      { id: 'mangalDosh',             label: '⚡ Mangal Dosh Check'    },
      { id: 'nadiDosha',              label: '🔴 Nadi Dosha'           },
      { id: 'emotionalCompatibility', label: '❤ Emotional Match'      },
      { id: 'careerCompatibility',    label: '💼 Career Compatibility' },
      { id: 'physicalCompatibility',  label: '💪 Physical Match'       },
      { id: 'longTermPrediction',     label: '🔮 Long Term Prediction' },
      { id: 'advice',                 label: '💬 Marriage Advice'      },
    ],
  },
  horoscopePDF: {
    label: 'Horoscope PDF', icon: '📄', mode: 'horoscope',
    extraFields: ['reportType'],
    buttons: [
      { id: 'generateHoroscope', label: '📄 Generate Horoscope Report' },
      { id: 'downloadHoroscope', label: '⬇ Download PDF'              },
    ],
  },
  tenYearPDF: {
    label: '10 Year Life PDF', icon: '📅', mode: 'tenyear',
    extraFields: ['reportType'],
    buttons: [
      { id: 'generate10Year', label: '📅 Generate 10 Year Report' },
      { id: 'download10Year', label: '⬇ Download PDF'            },
    ],
  },
};

// ── Service field requirements & chart flag ────────────────────────────────────
// fields   : which client fields to collect before starting
// needsChart: true → calls /analyze (birth chart required)
//             false → "Start Session" instantly, no backend chart call
// btnLabel : text on the action button
const SERVICE_REQUIREMENTS = {
  pastPrediction:     { fields: ['name','dob','tob','pob','gender','language'], needsChart: true,  btnLabel: 'Analyze Chart'   },
  futureConsultation: { fields: ['name','dob','tob','pob','gender','language'], needsChart: true,  btnLabel: 'Analyze Chart'   },
  remedies:           { fields: ['name','dob','tob','pob','gender','language'], needsChart: true,  btnLabel: 'Analyze Chart'   },
  palmistry:          { fields: ['name','gender','language'],                   needsChart: false, btnLabel: 'Start Session'   },
  astroVastu:         { fields: ['name','language'],                            needsChart: false, btnLabel: 'Start Session'   },
  numerology:         { fields: ['name','dob','language'],                      needsChart: true,  btnLabel: 'Analyze Chart'   },
  tarot:              { fields: ['name','gender','language'],                   needsChart: false, btnLabel: 'Start Session'   },
  compatibility:      { fields: ['name','dob','tob','pob','gender','language'], needsChart: true,  btnLabel: 'Analyze Charts'  },
  horoscopePDF:       { fields: ['name','dob','tob','pob','gender','language'], needsChart: true,  btnLabel: 'Generate Report' },
  tenYearPDF:         { fields: ['name','dob','tob','pob','gender','language'], needsChart: true,  btnLabel: 'Generate Report' },
};


// ── Default extra-data shape (all service-specific fields) ────────────────────
const DEFAULT_EXTRA_DATA = {
  fullName:        '',
  currentName:     '',
  clientQuestion:  '',
  concern:         '',
  tarotArea:       'General',
  tarotTimeframe:  'Current situation',
  doorDirection:   'North',
  propertyType:    'Home',
  roomConcerned:   '',
  problems:        '',
  purpose:         'Marriage',
  reportType:      'Detailed',
  rightHandPhoto:  null,
  leftHandPhoto:   null,
  housePhoto:      null,
  floorPlan:       null,
  person2:         { name: '', dob: '', tob: '', pob: '', gender: 'Female' },
};

// ── Session factory ────────────────────────────────────────────────────────────
const createSession = (initialClientData, svcKey, initialExtraData) => ({
  tabId:           `tab-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  sessionId:       `vtd-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  clientData:      initialClientData
    ? { ...initialClientData }
    : { name: '', dob: '', tob: '', pob: '', gender: 'Male', language: 'Telugu' },
  service:         svcKey || 'pastPrediction',
  sessionSeconds:  0,
  sessionEnded:    false,
  aiData:          null,
  aiLoading:       false,
  aiError:         null,
  activeSessionId: null,
  chatQuestion:    '',
  chatHistory:     [],
  chatLoading:     false,
  chatError:       null,
  sectionBtnActive: null,
  sectionBtnDone:   [],
  globalLang:      (() => {
    const raw = (initialClientData?.language || 'Telugu').toLowerCase().replace(' ', '-');
    return raw;
  })(),
  extraData: initialExtraData
    ? { ...DEFAULT_EXTRA_DATA, ...initialExtraData, fullName: initialExtraData.fullName || initialClientData?.name || '' }
    : { ...DEFAULT_EXTRA_DATA, fullName: initialClientData?.name || '' },
  notes:                '',
  rightHandPhotoBase64: null,  // compressed base64 for right hand (stored for entire session)
  leftHandPhotoBase64:  null,  // compressed base64 for left hand
  showSummaryModal: false,
  summaryData:      null,
  summaryLearnings: '',
  summaryLoading:   false,
});

// ── Main Component ─────────────────────────────────────────────────────────────
const AstrologerPanel = ({ service, clientData, initialExtraData, onChangeService, onLogout }) => {

  // ── Multi-session state ──────────────────────────────────────────────────────
  const [sessions,            setSessions]            = useState(() => [createSession(clientData, service, initialExtraData)]);
  const [activeTab,           setActiveTab]           = useState(0);
  const [connected,           setConnected]           = useState(false);
  const [confirmClose,        setConfirmClose]        = useState(null);
  const [showNewSessionModal, setShowNewSessionModal] = useState(false);
  const [showMobileMenu,      setShowMobileMenu]      = useState(false);
  const [showMobileNotes,     setShowMobileNotes]     = useState(false);
  const [showEngineReport,    setShowEngineReport]    = useState(false);

  const activeTabRef   = useRef(0);
  const sessionsRef    = useRef(sessions);
  const socketRef      = useRef(null);
  const timerRef       = useRef(null);
  const chatHistoryRef = useRef(null);

  // Keep refs current
  useEffect(() => { activeTabRef.current = activeTab; }, [activeTab]);
  useEffect(() => { sessionsRef.current  = sessions;  }, [sessions]);

  // (body scroll lock removed — caused entire page to freeze on mobile)

  const sess   = sessions[activeTab] || sessions[0];
  const config = SERVICE_CONFIGS[sess?.service || service] || SERVICE_CONFIGS.pastPrediction;
  const fmt    = s => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  // ── Update a specific tab by index (functional form supported) ────────────
  const updateTab = useCallback((tabIdx, updater) => {
    setSessions(prev => prev.map((s, i) =>
      i === tabIdx
        ? { ...s, ...(typeof updater === 'function' ? updater(s) : updater) }
        : s
    ));
  }, []);

  // Shorthand: update the currently active tab
  const setSess = useCallback((updater) => {
    setSessions(prev => prev.map((s, i) =>
      i === activeTabRef.current
        ? { ...s, ...(typeof updater === 'function' ? updater(s) : updater) }
        : s
    ));
  }, []);

  // ── Global timer — increments active session only ─────────────────────────
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setSessions(prev => prev.map((s, i) =>
        i === activeTabRef.current && !s.sessionEnded
          ? { ...s, sessionSeconds: s.sessionSeconds + 1 }
          : s
      ));
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, []);

  // ── Socket (single connection for all tabs) ───────────────────────────────
  useEffect(() => {
    const socket = io(API_BASE, { withCredentials: true });
    socketRef.current = socket;
    socket.on('connect', () => {
      setConnected(true);
      sessionsRef.current.forEach(s => socket.emit('join_room', { room: s.sessionId }));
    });
    socket.on('disconnect', () => setConnected(false));
    return () => socket.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Auto-scroll chat ──────────────────────────────────────────────────────
  useEffect(() => {
    if (chatHistoryRef.current) {
      chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight;
    }
  }, [sess.chatHistory, sess.chatLoading]);

  // ── Tab management ────────────────────────────────────────────────────────
  const addTab = useCallback(() => {
    setSessions(prev => {
      if (prev.length >= 6) return prev;
      return prev;
    });
    setShowNewSessionModal(true);
  }, []);

  const confirmNewSession = useCallback((svc, newClientData, extra) => {
    setSessions(prev => {
      if (prev.length >= 6) return prev;
      const newSess = createSession(newClientData, svc, extra);
      if (socketRef.current?.connected) {
        socketRef.current.emit('join_room', { room: newSess.sessionId });
      }
      const nextIdx = prev.length;
      setTimeout(() => setActiveTab(nextIdx), 0);
      return [...prev, newSess];
    });
    setShowNewSessionModal(false);
  }, []);

  const closeTab = useCallback((tabIdx) => setConfirmClose(tabIdx), []);

  const confirmCloseTab = useCallback(() => {
    const idx = confirmClose;
    setSessions(prev => {
      const updated = prev.filter((_, i) => i !== idx);
      return updated.length > 0 ? updated : [createSession(null, service)];
    });
    setActiveTab(prev => Math.max(0, prev > idx ? prev - 1 : prev === idx ? Math.max(0, idx - 1) : prev));
    setConfirmClose(null);
  }, [confirmClose, service]);

  // ── Parse birth details from current session ──────────────────────────────
  const parseBirth = useCallback(() => {
    const d = sessionsRef.current[activeTabRef.current]?.clientData;
    if (!d?.dob) throw new Error('Date of birth is required.');
    const [y, m, day] = d.dob.split('-');
    const [h, mn]     = (d.tob || '00:00').split(':');
    return {
      name: d.name, gender: d.gender, place: d.pob,
      day: parseInt(day, 10), month: parseInt(m, 10), year: parseInt(y, 10),
      hour: parseInt(h, 10), min: parseInt(mn, 10),
      lat: 0, lon: 0, tzone: 5.5,
    };
  }, []);

  // ── Chart Analysis / Session Start ───────────────────────────────────────
  const runAnalysis = useCallback(async () => {
    const tabIdx  = activeTabRef.current;
    const curSess = sessionsRef.current[tabIdx];
    const svcKey  = curSess.service || service;
    const svcReq  = SERVICE_REQUIREMENTS[svcKey] || SERVICE_REQUIREMENTS.pastPrediction;

    // Non-chart services (palmistry, tarot, vastu): no backend call needed —
    // just mark session as started so section buttons become enabled.
    if (!svcReq.needsChart) {
      updateTab(tabIdx, {
        aiData:    { service: svcKey, noChart: true, sessionId: curSess.sessionId },
        aiLoading: false,
        aiError:   null,
      });
      return;
    }

    // Chart services: call /analyze with birth details
    updateTab(tabIdx, { aiLoading: true, aiError: null });
    try {
      const payload = parseBirth();
      const res = await fetch(`${API_BASE}/api/ai-astrologer/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId: curSess.sessionId, ...payload }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || `HTTP ${res.status}`); }
      const data = await res.json();
      updateTab(tabIdx, { aiData: data, aiLoading: false, ...(data?.sessionId ? { activeSessionId: data.sessionId } : {}) });
    } catch (e) {
      updateTab(tabIdx, { aiError: e.message, aiLoading: false });
    }
  }, [parseBirth, updateTab, service]);

  // ── Helper: compress image via canvas (max 800×800, quality 0.8) ────────────
  const compressImage = useCallback((file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = ev => {
        const img = new Image();
        img.onload = () => {
          const MAX = 800;
          let { width, height } = img;
          if (width > MAX || height > MAX) {
            if (width > height) { height = Math.round(height * MAX / width); width = MAX; }
            else                { width  = Math.round(width  * MAX / height); height = MAX; }
          }
          const canvas = document.createElement('canvas');
          canvas.width  = width;
          canvas.height = height;
          canvas.getContext('2d').drawImage(img, 0, 0, width, height);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
          resolve(dataUrl.split(',')[1]); // return base64 only
        };
        img.onerror = () => resolve(null);
        img.src = ev.target.result;
      };
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(file);
    });
  }, []);

  // ── Helper: get both palm photos as base64 ───────────────────────────────────
  const getPalmPhotos = useCallback(async (tabIdx, curSess) => {
    let right = curSess.rightHandPhotoBase64 || null;
    let left  = curSess.leftHandPhotoBase64  || null;
    // Compress from File if not yet in state
    if (!right && curSess.extraData?.rightHandPhoto) {
      right = await compressImage(curSess.extraData.rightHandPhoto);
      if (right) updateTab(tabIdx, { rightHandPhotoBase64: right });
    }
    if (!left && curSess.extraData?.leftHandPhoto) {
      left = await compressImage(curSess.extraData.leftHandPhoto);
      if (left) updateTab(tabIdx, { leftHandPhotoBase64: left });
    }
    return { right, left };
  }, [updateTab, compressImage]);

  // ── Section button click → /predict-section (chart) or /chat (no-chart) ─────
  const handleSectionBtnClick = useCallback(async (sectionId) => {
    const tabIdx  = activeTabRef.current;
    const curSess = sessionsRef.current[tabIdx];
    if (!curSess.aiData) {
      updateTab(tabIdx, s => ({ chatHistory: [...s.chatHistory, { id: Date.now(), type: 'system', text: 'Please click "Analyze Chart" first.' }] }));
      return;
    }
    if (curSess.chatLoading) return;

    updateTab(tabIdx, s => ({
      sectionBtnActive: sectionId, chatError: null, chatLoading: true,
      chatHistory: [...s.chatHistory, { id: Date.now(), type: 'user', text: `▶ ${sectionId}` }],
    }));

    // ── No-chart services (palmistry / tarot / vastu / numerology) → /chat ────
    if (curSess.aiData.noChart) {
      const svcKey    = curSess.service;
      const svcCfg    = SERVICE_CONFIGS[svcKey] || SERVICE_CONFIGS.pastPrediction;
      const mode      = svcCfg.mode;
      const btnLabel  = svcCfg.buttons?.find(b => b.id === sectionId)?.label || sectionId;
      const { clientData: cd, extraData: ed, globalLang } = curSess;

      // Build a context-rich question for the AI
      let q = `Section: ${btnLabel}\nClient: ${cd.name}`;
      if (cd.gender)    q += ` (${cd.gender})`;
      if (svcKey === 'palmistry' && ed.concern) q += `\nClient's Concern: ${ed.concern}`;
      if (svcKey === 'tarot') {
        if (ed.tarotArea)      q += `\nReading Area: ${ed.tarotArea}`;
        if (ed.tarotTimeframe) q += `\nTime Frame: ${ed.tarotTimeframe}`;
        if (ed.clientQuestion) q += `\nClient's Question: ${ed.clientQuestion}`;
      }
      if (svcKey === 'astroVastu') {
        if (ed.doorDirection)  q += `\nMain Door Direction: ${ed.doorDirection}`;
        if (ed.propertyType)   q += `\nProperty Type: ${ed.propertyType}`;
        if (ed.roomConcerned)  q += `\nArea Concerned: ${ed.roomConcerned}`;
        if (ed.problems)       q += `\nProblems: ${ed.problems}`;
      }
      if (svcKey === 'numerology' && cd.dob) q += `\nDate of Birth: ${cd.dob}`;
      q += `\n\nPlease provide ${btnLabel} for this client.`;

      try {
        const sid = curSess.activeSessionId || curSess.sessionId;
        const noChartBody = {
          question: q, sessionId: sid,
          birthDetails: { name: cd.name, gender: cd.gender },
          section: sectionId, mode, language: globalLang,
        };
        if (svcKey === 'palmistry') {
          const { right, left } = await getPalmPhotos(tabIdx, curSess);
          if (right) { noChartBody.rightHandPhotoBase64 = right; noChartBody.palmMediaType = 'image/jpeg'; }
          if (left)  { noChartBody.leftHandPhotoBase64  = left; }
        }
        const res = await fetch(`${API_BASE}/api/ai-astrologer/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(noChartBody),
        });
        if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || `HTTP ${res.status}`); }
        const data  = await res.json();
        const rawV  = data.verificationQuestions || data.verify || [];
        const verify = rawV.map(vq => typeof vq === 'string' ? vq : (vq.question || vq.text || '')).filter(Boolean);
        updateTab(tabIdx, s => ({
          chatLoading: false,
          sectionBtnDone: [...new Set([...s.sectionBtnDone, sectionId])],
          chatHistory: [...s.chatHistory, {
            id: Date.now() + 1, type: 'ai', section: sectionId, lang: globalLang,
            english: data.english || data.prediction || '', telugu: data.telugu || '',
            periods: [], traits: [], events: [], verify,
            confidence: data.confidence || null,
          }],
        }));
        if (socketRef.current?.connected) {
          socketRef.current.emit('prediction-complete', { room: curSess.sessionId, section: sectionId });
        }
      } catch (e) {
        updateTab(tabIdx, { chatError: e.message, chatLoading: false });
      }
      return;
    }

    // ── Chart-based services → /predict-section ───────────────────────────────
    try {
      const sid = curSess.activeSessionId || curSess.sessionId;
      const res = await fetch(`${API_BASE}/api/ai-astrologer/predict-section`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: sid, section: sectionId,
          language: curSess.globalLang, mode: config.mode,
          extraData: {
            ...curSess.extraData,
            // strip File objects — can't serialize
            palmPhoto: undefined, housePhoto: undefined, floorPlan: undefined,
          },
        }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || `HTTP ${res.status}`); }
      const data  = await res.json();
      const rawV  = data.verificationQuestions || data.verify || [];
      const verify = rawV.map(vq => typeof vq === 'string' ? vq : (vq.question || vq.text || '')).filter(Boolean);

      // PDF download sections — trigger client-side PDF generation
      if (sectionId === 'downloadHoroscope' || sectionId === 'download10Year') {
        const { generatePDF } = await import('../../utils/pdfGenerator');
        const reportSection   = sectionId === 'downloadHoroscope' ? 'generateHoroscope' : 'generate10Year';
        const reportMsg       = curSess.chatHistory.slice().reverse().find(m => m.section === reportSection);
        if (reportMsg) {
          generatePDF(curSess.clientData, reportMsg.english || reportMsg.telugu || '', sectionId === 'download10Year');
        }
      }

      updateTab(tabIdx, s => ({
        chatLoading: false,
        sectionBtnDone: [...new Set([...s.sectionBtnDone, sectionId])],
        chatHistory: [...s.chatHistory, {
          id: Date.now() + 1, type: 'ai', section: sectionId, lang: curSess.globalLang,
          english: data.prediction || data.english || '', telugu: data.telugu || '',
          periods: data.periods || [], traits: data.traits || [], events: data.events || [], verify,
          confidence: data.confidence || null,
          d1Chart: data.d1Chart || null, d9Chart: data.d9Chart || null, dashaFull: data.dashaFull || null,
        }],
      }));
      if (socketRef.current?.connected) {
        socketRef.current.emit('prediction-complete', { room: curSess.sessionId, section: sectionId });
      }
    } catch (e) {
      updateTab(tabIdx, { chatError: e.message, chatLoading: false });
    }
  }, [updateTab, config.mode, getPalmPhotos]);

  // ── Future topic predict → /future-predict ────────────────────────────────
  const handleFutureTopic = useCallback(async (topicId, customQuestion) => {
    const tabIdx  = activeTabRef.current;
    const curSess = sessionsRef.current[tabIdx];
    if (!curSess.aiData || curSess.chatLoading) return;
    updateTab(tabIdx, s => ({
      chatError: null, chatLoading: true,
      chatHistory: [...s.chatHistory, { id: Date.now(), type: 'user', text: customQuestion || `▶ ${topicId}` }],
    }));
    try {
      const sid = curSess.activeSessionId || curSess.sessionId;
      const res = await fetch(`${API_BASE}/api/ai-astrologer/future-predict`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId: sid, topic: topicId, language: curSess.globalLang, customQuestion }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || `HTTP ${res.status}`); }
      const data = await res.json();
      updateTab(tabIdx, s => ({
        chatLoading: false,
        chatHistory: [...s.chatHistory, {
          id: Date.now() + 1, type: 'ai', section: topicId, lang: curSess.globalLang,
          english: data.english || '', telugu: data.telugu || '',
          confidence: data.confidence || null, followUp: data.followUp || [],
          periods: [], traits: [], events: [], verify: [],
        }],
      }));
    } catch (e) { updateTab(tabIdx, { chatError: e.message, chatLoading: false }); }
  }, [updateTab]);

  // ── Free-form chat → /chat ─────────────────────────────────────────────────
  const sendChatQuestion = useCallback(async (quickQ, sectionId, modeValue, confirmedEvents) => {
    const tabIdx  = activeTabRef.current;
    const curSess = sessionsRef.current[tabIdx];
    const question = (quickQ || curSess.chatQuestion).trim();
    if (!question || curSess.chatLoading) return;
    updateTab(tabIdx, s => ({
      chatQuestion: '', chatError: null, chatLoading: true,
      chatHistory: [...s.chatHistory, { id: Date.now(), type: 'user', text: question }],
    }));
    try {
      const sid  = curSess.activeSessionId || curSess.sessionId;
      const body = {
        question, sessionId: sid,
        birthDetails: { name: curSess.clientData.name, dob: curSess.clientData.dob, tob: curSess.clientData.tob, pob: curSess.clientData.pob },
        section: sectionId || 'general',
        mode:    modeValue || config.mode || 'past',
        language: curSess.globalLang,
      };
      if (confirmedEvents) body.confirmedEvents = [confirmedEvents];
      if (curSess.service === 'palmistry') {
        const { right, left } = await getPalmPhotos(tabIdx, curSess);
        if (right) { body.rightHandPhotoBase64 = right; body.palmMediaType = 'image/jpeg'; }
        if (left)  { body.leftHandPhotoBase64  = left; }
      }
      const res = await fetch(`${API_BASE}/api/ai-astrologer/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || `HTTP ${res.status}`); }
      const data = await res.json();
      updateTab(tabIdx, s => ({
        chatLoading: false,
        chatHistory: [...s.chatHistory, {
          id: Date.now() + 1, type: 'ai', lang: curSess.globalLang,
          english: data.english || '', telugu: data.telugu || '',
          periods: data.periods || [], traits: data.traits || [],
          events:  data.events  || [], verify: data.verify || [],
          confidence: data.confidence || null,
        }],
      }));
    } catch (e) { updateTab(tabIdx, { chatError: e.message, chatLoading: false }); }
  }, [updateTab, config.mode, getPalmPhotos]);

  // ── Palm photo → /analyze-palm (Claude vision) ────────────────────────────
  // handType: 'right' | 'left'
  const handlePalmAnalysis = useCallback(async (imageBase64, mediaType, handType = 'right') => {
    const tabIdx  = activeTabRef.current;
    const curSess = sessionsRef.current[tabIdx];
    const handLabel = handType === 'left' ? '🤚 Left Hand' : '✋ Right Hand';

    // Save compressed base64 to session state
    const stateKey = handType === 'left' ? 'leftHandPhotoBase64' : 'rightHandPhotoBase64';
    updateTab(tabIdx, s => ({
      [stateKey]: imageBase64,
      chatLoading: true,
      chatError:   null,
      chatHistory: [...s.chatHistory, { id: Date.now(), type: 'user', text: `📷 ${handLabel} uploaded — analyzing...` }],
    }));

    try {
      const sid = curSess.activeSessionId || curSess.sessionId;

      // Get the other hand's photo too (may already be stored)
      const otherKey = handType === 'left' ? 'rightHandPhotoBase64' : 'leftHandPhotoBase64';
      const otherB64 = curSess[otherKey] || null;

      // Store both photos in backend session (fire-and-forget)
      fetch(`${API_BASE}/api/ai-astrologer/store-palm-photos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId:    sid,
          rightHandB64: handType === 'right' ? imageBase64 : otherB64,
          leftHandB64:  handType === 'left'  ? imageBase64 : otherB64,
          mediaType:    mediaType || 'image/jpeg',
        }),
      }).catch(() => {});

      const res = await fetch(`${API_BASE}/api/ai-astrologer/analyze-palm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          imageBase64,
          mediaType,
          handType,
          gender:    curSess.clientData.gender,
          concern:   curSess.extraData.concern || '',
          section:   handType === 'left' ? 'leftHandPhoto' : 'rightHandPhoto',
          language:  curSess.globalLang,
          sessionId: sid,
        }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || `HTTP ${res.status}`); }
      const data  = await res.json();
      const rawV  = data.verify || [];
      const verify = rawV.map(vq => typeof vq === 'string' ? vq : (vq.question || vq.text || '')).filter(Boolean);
      const sectionId = handType === 'left' ? 'leftHandPhoto' : 'rightHandPhoto';
      updateTab(tabIdx, s => ({
        chatLoading:   false,
        [stateKey]:    imageBase64,
        sectionBtnDone: [...new Set([...s.sectionBtnDone, sectionId])],
        chatHistory: [...s.chatHistory, {
          id: Date.now() + 1, type: 'ai', section: sectionId, lang: curSess.globalLang,
          english: data.english || '', telugu: data.telugu || '',
          periods: [], traits: [], events: [], verify,
          confidence: data.confidence || null,
        }],
      }));
    } catch (e) {
      updateTab(tabIdx, { chatError: e.message, chatLoading: false });
    }
  }, [updateTab]);

  // ── Mobile palm upload: compress file + store preview + trigger analysis ─────
  const handleMobilePalmUpload = useCallback(async (file, hand) => {
    if (!file) return;
    const base64 = await compressImage(file);
    if (!base64) return;
    const dataUrl = `data:image/jpeg;base64,${base64}`;
    const urlKey  = hand === 'right' ? 'rightHandPreviewUrl' : 'leftHandPreviewUrl';
    setSess(s => ({ extraData: { ...s.extraData, [urlKey]: dataUrl } }));
    handlePalmAnalysis(base64, 'image/jpeg', hand);
  }, [compressImage, setSess, handlePalmAnalysis]);

  // ── Retranslate bubble ────────────────────────────────────────────────────
  const handleRetranslate = useCallback(async (bubbleId, sectionId, lang) => {
    const tabIdx  = activeTabRef.current;
    const curSess = sessionsRef.current[tabIdx];
    if (!curSess.aiData || !sectionId) return;
    updateTab(tabIdx, s => ({ chatHistory: s.chatHistory.map(e => e.id === bubbleId ? { ...e, retransLoading: true } : e) }));
    try {
      const sid = curSess.activeSessionId || curSess.sessionId;
      const res = await fetch(`${API_BASE}/api/ai-astrologer/predict-section`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId: sid, section: sectionId, language: lang }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data  = await res.json();
      const rawVfy = data.verificationQuestions || data.verify || [];
      const verify = rawVfy.map(q => typeof q === 'string' ? q : (q.question || q.text || '')).filter(Boolean);
      updateTab(tabIdx, s => ({
        chatHistory: s.chatHistory.map(e => e.id === bubbleId ? {
          ...e, lang, retransLoading: false,
          english: data.prediction || data.english || '', telugu: data.telugu || '',
          periods: data.periods || [], traits: data.traits || [], events: data.events || [], verify,
          confidence: data.confidence || null,
        } : e),
      }));
    } catch {
      updateTab(tabIdx, s => ({ chatHistory: s.chatHistory.map(e => e.id === bubbleId ? { ...e, retransLoading: false } : e) }));
    }
  }, [updateTab]);

  // ── Summary modal ─────────────────────────────────────────────────────────
  const openSummaryModal = useCallback(async () => {
    const tabIdx  = activeTabRef.current;
    const curSess = sessionsRef.current[tabIdx];
    if (curSess.sessionEnded) return;
    updateTab(tabIdx, { showSummaryModal: true, summaryLoading: true });
    try {
      if (curSess.activeSessionId) {
        const r = await fetch(`${API_BASE}/api/training/session/${curSess.activeSessionId}`);
        if (r.ok) { const d = await r.json(); updateTab(tabIdx, { summaryData: d, summaryLoading: false }); }
        else updateTab(tabIdx, { summaryLoading: false });
      } else {
        updateTab(tabIdx, { summaryLoading: false });
      }
    } catch { updateTab(tabIdx, { summaryLoading: false }); }
  }, [updateTab]);

  const endSession = useCallback(() => {
    const tabIdx  = activeTabRef.current;
    const curSess = sessionsRef.current[tabIdx];
    if (curSess.sessionEnded) return;
    socketRef.current?.emit('session-end', { room: curSess.sessionId });
    updateTab(tabIdx, { sessionEnded: true, showSummaryModal: false });
  }, [updateTab]);

  const saveAndEnd = useCallback(async () => {
    const tabIdx  = activeTabRef.current;
    const curSess = sessionsRef.current[tabIdx];
    if (curSess.activeSessionId) {
      const confirmed = (curSess.summaryData?.predictions || []).filter(p => p.verification?.response === 'yes').length;
      const total     = (curSess.summaryData?.predictions || []).length;
      const accuracy  = total > 0 ? Math.round((confirmed / total) * 100) : null;
      try {
        await fetch(`${API_BASE}/api/training/finalize`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId: curSess.activeSessionId, notes: curSess.summaryLearnings, learnings: curSess.summaryLearnings ? [curSess.summaryLearnings] : [], accuracy }),
        });
        const r = await fetch(`${API_BASE}/api/training/session/${curSess.activeSessionId}`);
        if (r.ok) {
          const blob = await r.blob();
          const url  = URL.createObjectURL(blob);
          const a    = document.createElement('a');
          a.href = url; a.download = `vtd-session-${curSess.activeSessionId}.json`;
          document.body.appendChild(a); a.click(); document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }
      } catch {}
    }
    endSession();
  }, [endSession]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeLang = useCallback((lang) => {
    setSess({ globalLang: lang });
    localStorage.setItem('vtd_lang', lang);
  }, [setSess]);

  // ── Mobile: download report without SessionNotes component ───────────────────
  const downloadMobileReport = useCallback(() => {
    const curSess = sessionsRef.current[activeTabRef.current];
    const cd      = curSess.clientData;
    const cfg     = SERVICE_CONFIGS[curSess.service] || SERVICE_CONFIGS.pastPrediction;
    const date    = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    const timer   = fmt(curSess.sessionSeconds);
    const predictions = curSess.chatHistory
      .filter(m => m.type === 'ai')
      .map((m, idx) => `[${idx + 1}] ${(m.section || 'General').toUpperCase()}\n${(m.english || m.telugu || '').slice(0, 800)}`)
      .join('\n\n---\n\n');
    const lines = [
      'VTD ASTROLOGY — SESSION REPORT',
      '================================',
      `Date       : ${date}`,
      `Duration   : ${timer}`,
      `Service    : ${cfg.label}`,
      `Client     : ${cd.name || '—'} · ${cd.gender || '—'} · ${cd.language || '—'}`,
      `DOB        : ${cd.dob || '—'}${cd.tob ? ' · ' + cd.tob : ''}`,
      `Place      : ${cd.pob || '—'}`,
      '',
      'PREDICTIONS',
      '-----------',
      predictions || '— None recorded —',
      '',
      'NOTES',
      '-----',
      curSess.notes || '— None —',
      '',
      '================================',
      'CONFIDENTIAL — Internal VTD use only.',
      '© VTD Astrology · vtdastrology.com',
    ].join('\n');
    const blob = new Blob([lines], { type: 'text/plain;charset=utf-8' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `VTD-${(cd.name || 'Session').replace(/\s+/g, '-')}-${date.replace(/\s+/g, '-')}.txt`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setShowMobileMenu(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fmt]);

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="ap-panel">

      {/* Top bar */}
      <header className="ap-topbar">
        <button className="ap-topbar__back" onClick={onChangeService}>← Change</button>

        <div className="ap-topbar__center">
          {/* Desktop: service + connection */}
          <span className="ap-topbar__svc ap-desktop-only">{config.icon} {config.label}</span>
          <span className="ap-topbar__dot ap-desktop-only" />
          <span className={`ap-conn ap-desktop-only${connected ? ' ap-conn--live' : ''}`}>
            <span className="ap-conn__dot" />
            {connected ? 'Live' : 'Connecting'}
          </span>
          {/* Mobile: client name · service */}
          <span className="ap-topbar__mobile-info ap-mobile-only">
            {sess.clientData?.name
              ? `${sess.clientData.name.split(' ')[0]} · ${config.label}`
              : config.label
            }
          </span>
          {/* Mobile: session timer */}
          <span className="ap-topbar__mobile-timer ap-mobile-only">{fmt(sess.sessionSeconds)}</span>
        </div>

        {/* Mobile menu button */}
        <button
          className="ap-topbar__menu-btn ap-mobile-only"
          onClick={() => setShowMobileMenu(v => !v)}
          aria-label="Open menu"
        >⋮</button>

        <button className="ap-topbar__logout" style={{marginRight:'6px'}} onClick={() => setShowEngineReport(true)}>📊 Engine Report</button>
        <button className="ap-topbar__logout" onClick={onLogout}>Logout</button>
      </header>

      {/* ── Session Tabs ── */}
      <div className="ap-tabs">
        <div className="ap-tabs__list">
          {sessions.map((s, idx) => {
            const firstName  = (s.clientData?.name?.trim() || 'New').split(' ')[0];
            const svcIcon    = SERVICE_CONFIGS[s.service]?.icon || '📖';
            const svcShort   = SERVICE_SHORT_NAMES[s.service] || 'Session';
            const tabTitle   = `${svcIcon} ${firstName} - ${svcShort}`;
            return (
              <div
                key={s.tabId}
                className={`ap-tab${idx === activeTab ? ' ap-tab--active' : ''}${s.sessionEnded ? ' ap-tab--ended' : ''}`}
                onClick={() => setActiveTab(idx)}
                title={s.clientData?.name?.trim() || 'New Session'}
              >
                <span className="ap-tab__name">{tabTitle}</span>
                {s.notes && <span className="ap-tab__unsaved" title="Notes not saved">•</span>}
                {s.sessionEnded && <span className="ap-tab__badge" title="Session ended">✓</span>}
                <button
                  className="ap-tab__close"
                  onClick={e => { e.stopPropagation(); closeTab(idx); }}
                  title="Close tab"
                >×</button>
              </div>
            );
          })}
        </div>
        {sessions.length < 6
          ? (
            <button className="ap-tabs__new" onClick={addTab} title="Open new client session">
              + New Session
            </button>
          ) : (
            <span className="ap-tabs__max" title="Close a session to open a new one">
              Max 6 tabs
            </span>
          )
        }
      </div>

      {/* 3-column body */}
      <div className="ap-columns">

        <LeftPanel
          key={sess.tabId}
          clientData={sess.clientData}
          onUpdateClientData={updates => setSess(s => ({
            clientData: { ...s.clientData, ...updates },
            ...(updates.name !== undefined ? { extraData: { ...s.extraData, fullName: updates.name } } : {}),
          }))}
          config={config}
          svcReq={SERVICE_REQUIREMENTS[sess.service || service] || SERVICE_REQUIREMENTS.pastPrediction}
          extraData={sess.extraData}
          setExtraData={updater => setSess(s => ({ extraData: typeof updater === 'function' ? updater(s.extraData) : updater }))}
          aiData={sess.aiData}
          aiLoading={sess.aiLoading}
          aiError={sess.aiError}
          onAnalyze={runAnalysis}
          onAnalyzeRightHand={(b64, mt) => handlePalmAnalysis(b64, mt, 'right')}
          onAnalyzeLeftHand={(b64, mt)  => handlePalmAnalysis(b64, mt, 'left')}
        />

        <AiChatbot
          aiData={sess.aiData}
          chatHistory={sess.chatHistory}
          chatLoading={sess.chatLoading}
          chatError={sess.chatError}
          chatQuestion={sess.chatQuestion}
          setChatQuestion={q => setSess(s => ({ chatQuestion: typeof q === 'function' ? q(s.chatQuestion) : q }))}
          onSend={sendChatQuestion}
          onClearChat={() => setSess({ chatHistory: [] })}
          onVerify={(bubbleId, _qIdx, answer, questionText) => {
            if (answer === 'yes') {
              sendChatQuestion(`Client confirmed: ${questionText}`, 'general', config.mode || 'past', `Verified: ${questionText}`);
            }
          }}
          chatHistoryRef={chatHistoryRef}
          sectionBtnActive={sess.sectionBtnActive}
          sectionBtnDone={new Set(sess.sectionBtnDone)}
          onSectionBtnClick={handleSectionBtnClick}
          analysisReady={!!sess.aiData}
          globalLang={sess.globalLang}
          onLangChange={changeLang}
          onRetranslate={handleRetranslate}
          onFutureTopic={handleFutureTopic}
          serviceButtons={config.buttons}
          serviceMode={config.mode}
          onMobileAnalyze={runAnalysis}
          analyzeLoading={sess.aiLoading}
          analyzeError={sess.aiError}
          clientData={sess.clientData}
          rightHandPreviewUrl={sess.extraData?.rightHandPreviewUrl || null}
          leftHandPreviewUrl={sess.extraData?.leftHandPreviewUrl  || null}
          onMobilePalmUpload={sess.service === 'palmistry' ? handleMobilePalmUpload : null}
          onSendAudio={(audioUrl) => {
            const tabIdx = activeTabRef.current;
            updateTab(tabIdx, s => ({
              chatHistory: [...s.chatHistory, {
                id: Date.now(), type: 'user', text: 'Voice message',
                audioUrl,
              }],
            }));
          }}
        />

        <SessionNotes
          clientData={sess.clientData}
          service={sess.service || service}
          chatHistory={sess.chatHistory}
          sessionTimer={fmt(sess.sessionSeconds)}
          aiData={sess.aiData}
          activeSessionId={sess.activeSessionId}
          onEnd={openSummaryModal}
          notes={sess.notes}
          onNotesChange={text => setSess({ notes: text })}
        />

      </div>

      {/* ── Mobile Menu Dropdown ── */}
      {showMobileMenu && (
        <div className="ap-mobile-menu-overlay" onClick={() => setShowMobileMenu(false)}>
          <div className="ap-mobile-menu" onClick={e => e.stopPropagation()}>
            <div
              className="ap-mobile-menu__item"
              onClick={() => { setShowMobileNotes(true); setShowMobileMenu(false); }}
            >
              📝 Notes{sess.notes ? ' •' : ''}
            </div>
            <div
              className="ap-mobile-menu__item"
              onClick={downloadMobileReport}
            >
              ⬇ Download Report
            </div>
            <div
              className="ap-mobile-menu__item ap-mobile-menu__item--danger"
              onClick={() => { openSummaryModal(); setShowMobileMenu(false); }}
            >
              ● End Session
            </div>
          </div>
        </div>
      )}

      {/* ── Mobile Notes Overlay ── */}
      {showMobileNotes && (
        <div className="ap-mobile-notes-overlay">
          <div className="ap-mobile-notes-header">
            <span className="ap-mobile-notes-title">Session Notes</span>
            <button className="ap-mobile-notes-close" onClick={() => setShowMobileNotes(false)}>✕</button>
          </div>
          <textarea
            className="ap-mobile-notes-textarea"
            value={sess.notes}
            onChange={e => setSess({ notes: e.target.value })}
            placeholder="Type notes during session... (auto-saved)"
            autoFocus
          />
          <div className="ap-mobile-notes-footer">
            <span className="ap-mobile-notes-hint">Auto-saved to session</span>
            <button className="ap-mobile-notes-done" onClick={() => setShowMobileNotes(false)}>Done</button>
          </div>
        </div>
      )}

      {/* ── Session Summary Modal ── */}
      {sess.showSummaryModal && (
        <div className="ap-summary-overlay" onClick={e => e.target === e.currentTarget && endSession()}>
          <div className="ap-summary-modal">
            <div className="ap-summary__header">
              <span className="ap-summary__title">SESSION LEARNING SUMMARY</span>
              <button className="ap-summary__close" onClick={endSession}>✕</button>
            </div>
            {sess.summaryLoading
              ? <div className="ap-summary__loading">Loading session data…</div>
              : (
                <>
                  <div className="ap-summary__stats">
                    {(() => {
                      const preds     = sess.summaryData?.predictions || [];
                      const confirmed = preds.filter(p => p.verification?.response === 'yes').length;
                      const partial   = preds.filter(p => p.verification?.response === 'partial').length;
                      const accuracy  = preds.length > 0 ? Math.round(((confirmed + partial * 0.5) / preds.length) * 100) : 0;
                      return (
                        <>
                          <div className="ap-summary__stat"><span>Sections predicted</span><strong>{preds.length}</strong></div>
                          <div className="ap-summary__stat"><span>Client confirmed ✓</span><strong className="ap-summary__stat--yes">{confirmed}</strong></div>
                          <div className="ap-summary__stat"><span>Partial match ~</span><strong className="ap-summary__stat--partial">{partial}</strong></div>
                          <div className="ap-summary__stat"><span>Session accuracy</span><strong className="ap-summary__stat--acc">{accuracy}%</strong></div>
                        </>
                      );
                    })()}
                  </div>
                  {sess.summaryData?.predictions?.filter(p => p.verification?.response === 'yes').length > 0 && (
                    <div className="ap-summary__patterns">
                      <div className="ap-summary__section-title">✓ Confirmed Sections</div>
                      {sess.summaryData.predictions
                        .filter(p => p.verification?.response === 'yes')
                        .map((p, i) => (
                          <div key={i} className="ap-summary__pattern">
                            ✅ {p.section} — {p.verification?.question?.slice(0, 60) || 'Confirmed'}
                          </div>
                        ))
                      }
                    </div>
                  )}
                  <div className="ap-summary__learn-block">
                    <div className="ap-summary__section-title">New Learnings / Notes</div>
                    <textarea
                      className="ap-summary__textarea"
                      rows={4}
                      placeholder="What did you learn from this session? Accuracy observations, new patterns noticed..."
                      value={sess.summaryLearnings}
                      onChange={e => setSess({ summaryLearnings: e.target.value })}
                    />
                  </div>
                  <div className="ap-summary__actions">
                    <button className="ap-summary__btn ap-summary__btn--save" onClick={saveAndEnd}>📥 Save & Download Report</button>
                    <button className="ap-summary__btn ap-summary__btn--close" onClick={endSession}>Close Session</button>
                  </div>
                </>
              )
            }
          </div>
        </div>
      )}

      {/* ── New Session Modal — shows ServiceSelect inside overlay ── */}
      {showNewSessionModal && (
        <div className="ap-newses-overlay" onClick={e => { if (e.target === e.currentTarget) setShowNewSessionModal(false); }}>
          <div className="ap-newses-modal">
            <div className="ap-newses-header">
              <span className="ap-newses-title">New Client Session</span>
              <button className="ap-newses-close" onClick={() => setShowNewSessionModal(false)}>✕</button>
            </div>
            {sessions.length >= 6
              ? (
                <div className="ap-newses-max">
                  <p>Maximum 6 sessions open at once.</p>
                  <p>Close an existing session tab to open a new one.</p>
                </div>
              ) : (
                <ServiceSelect
                  onStart={confirmNewSession}
                  onLogout={onLogout}
                />
              )
            }
          </div>
        </div>
      )}

      {/* ── Confirm close tab dialog ── */}
      {confirmClose !== null && (
        <div className="ap-summary-overlay" onClick={() => setConfirmClose(null)}>
          <div className="ap-confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="ap-confirm__title">Close Session?</div>
            <div className="ap-confirm__msg">
              Close "{sessions[confirmClose]?.clientData?.name?.trim() || 'New Session'}"?
              All unsaved data for this tab will be lost.
            </div>
            <div className="ap-confirm__actions">
              <button className="ap-confirm__btn ap-confirm__btn--yes" onClick={confirmCloseTab}>Close Tab</button>
              <button className="ap-confirm__btn ap-confirm__btn--no"  onClick={() => setConfirmClose(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Engine-based Past Predictions Report ── */}
      {showEngineReport && (
        <PastPredictionsReport onClose={() => setShowEngineReport(false)} />
      )}

    </div>
  );
};

export default AstrologerPanel;
